import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const STATUS_LABELS = {
  pagada: { label: 'Pagada', color: 'bg-emerald-100 text-emerald-700' },
  parcial: { label: 'Pago parcial', color: 'bg-amber-100 text-amber-700' },
  pendiente: { label: 'Pendiente', color: 'bg-sky-100 text-sky-700' },
  cancelada: { label: 'Cancelada', color: 'bg-gray-100 text-gray-500' },
};

const money = (n) => Number(n || 0).toLocaleString('es-PR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Vista pública de UNA factura — el link exacto (con el ID de 20 caracteres
// de Firestore) es lo que la protege, como un link secreto; no hay ninguna
// forma de listar o adivinar facturas de otro cliente desde aquí. No
// requiere sesión — es lo que se manda por correo para que el cliente la
// pueda ver sin tener que crear cuenta ni iniciar sesión.
export default function PublicInvoiceView() {
  const { clientId, invoiceId } = useParams();
  const [state, setState] = useState({ loading: true, invoice: null, error: '' });

  useEffect(() => {
    getDoc(doc(db, 'clients', clientId, 'invoices', invoiceId))
      .then((snap) => {
        if (!snap.exists()) {
          setState({ loading: false, invoice: null, error: 'No encontramos esta factura. Verifica el enlace o contáctanos.' });
          return;
        }
        setState({ loading: false, invoice: snap.data(), error: '' });
      })
      .catch(() => setState({ loading: false, invoice: null, error: 'No se pudo cargar la factura. Intenta de nuevo en unos minutos.' }));
  }, [clientId, invoiceId]);

  if (state.loading) {
    return <div className="flex min-h-screen items-center justify-center bg-gray-50 text-sm text-gray-400">Cargando factura…</div>;
  }

  if (state.error || !state.invoice) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="max-w-sm rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <img src="/logo-bbts.png?v=2" alt="BBTS" className="mx-auto mb-4 h-8" />
          <p className="text-sm text-gray-600">{state.error}</p>
        </div>
      </div>
    );
  }

  const inv = state.invoice;
  const items = Array.isArray(inv.items) ? inv.items : [];
  const status = STATUS_LABELS[inv.status] || STATUS_LABELS.pendiente;
  const dateLabel = inv.dateCreated?.toDate
    ? inv.dateCreated.toDate().toLocaleDateString('es-PR', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <div className="h-1.5 rounded-t-2xl bg-[#ff4400]" />
        <div className="rounded-b-2xl border border-t-0 border-gray-200 bg-white p-8 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-100 pb-6">
            <img src="/logo-bbts.png?v=2" alt="BBTS" className="h-9" />
            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${status.color}`}>{status.label}</span>
          </div>

          <div className="mt-6 flex flex-wrap justify-between gap-4 text-sm">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-gray-400">Facturado a</p>
              <p className="mt-1 font-semibold text-gray-800">{inv.clientName}</p>
              {inv.clientEmail && <p className="text-gray-500">{inv.clientEmail}</p>}
            </div>
            {dateLabel && (
              <div className="text-right">
                <p className="text-xs font-bold uppercase tracking-wide text-gray-400">Fecha</p>
                <p className="mt-1 text-gray-700">{dateLabel}</p>
              </div>
            )}
          </div>

          <table className="mt-8 w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-bold uppercase tracking-wide text-gray-400">
                <th className="pb-2">Descripción</th>
                <th className="pb-2 text-right">Monto</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={i} className="border-b border-gray-50">
                  <td className="py-3 pr-4">
                    <p className="font-semibold text-gray-800">{item.name}</p>
                    {item.description && <p className="mt-0.5 text-xs text-gray-500">{item.description}</p>}
                  </td>
                  <td className="py-3 text-right text-gray-700">${money(item.quantity * item.price)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 space-y-1.5 border-t border-gray-100 pt-4 text-sm">
            <div className="flex justify-between text-gray-500">
              <span>Total</span>
              <span>${money(inv.total)}</span>
            </div>
            {inv.discount > 0 && (
              <div className="flex justify-between text-gray-500">
                <span>Descuento</span>
                <span>-${money(inv.discount)}</span>
              </div>
            )}
            {(inv.payments || []).map((p, i) => (
              <div key={i} className="flex justify-between text-emerald-600">
                <span>Pago recibido{p.date ? ` (${p.date})` : ''}</span>
                <span>-${money(p.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between border-t border-gray-100 pt-2 text-base font-bold text-gray-900">
              <span>Balance pendiente</span>
              <span>${money(inv.remaining)}</span>
            </div>
          </div>

          <div className="mt-8 rounded-xl bg-gray-50 p-4 text-center text-sm text-gray-600">
            ¿Preguntas sobre esta factura? Llámanos al <strong>787-236-9189</strong> o
            {' '}<a href="https://wa.me/15558364840" className="font-semibold text-[#ff4400]">escríbenos por WhatsApp</a>.
          </div>
        </div>
        <p className="mt-4 text-center text-xs text-gray-400">BBTS Technology Solutions · info@bbtspr.com</p>
      </div>
    </div>
  );
}
